/**
 * The operation vocabulary — Stories 27.1 and 27.5, RULE-019 (PRD §3.5).
 *
 * "**the AI never generates UI or free-form output. It emits operations against the
 * funnel schema** — a fixed tool vocabulary (`add_step`, `add_question`, `set_option`,
 * `set_criterion`, `add_branch`, `set_band`, `set_outcome`, `set_persona`, `add_action`,
 * `add_extraction_field`, `set_icp_profile`) which are the *same* operations the human
 * builder UI calls. Consequences: every AI edit is undoable via the existing patch stack; an
 * AI-built funnel is structurally identical to a hand-built one; the model cannot produce an
 * invalid funnel."
 *
 * ELEVEN, AND THEIR NAMES ARE THE PRD'S. RULE-019 named nine before M2 built extraction (PRD
 * §3.6) and the ideal-customer fit (§3.8), so none of them wrote either and a one-shot funnel
 * could score closed answers only; DECISION-177 added the two that do (Story 27.5). Each is one
 * strict Zod object in one discriminated union, and that one definition has three readers:
 * `applyOperation` validates with it, a model's proposal is held to it before anyone sees it
 * (Story 27.3), and the builder calls the same function a proposal does (Story 27.2). So
 * validation and proposal cannot disagree, and neither can the two authors (PRD §3.2's single
 * definition). `jsonSchemaOf(operationSchema)` expresses it for a model too, but 27.3 measured
 * every Workers AI model breaking when its decoder was held to it, so the authoring prompt
 * names the keys instead (Q-136).
 *
 * TWO WAYS TO APPLY, one implementation under both:
 *
 *   `applyOperation(draft, op)` — one operation, in place. The draft is an Immer draft inside
 *   `edit()`, so a person's edit and an accepted proposal are patches on one stack (RULE-018).
 *   It refuses what it cannot do — an argument of the wrong type, an id that names nothing, an
 *   id already taken — and then writes nothing. It does NOT judge the whole funnel: the builder
 *   passes through states the save refuses, a weight typed one digit at a time or a band
 *   boundary mid-drag, and `editor/QualifyTab.tsx` says why that judgement belongs at save.
 *
 *   `applyOperations(funnel, ops)` — a sequence, on a copy: the model's path. Refused whole —
 *   one malformed operation and none is applied — and the result is returned only when
 *   `parseFunnel` accepts it, schema and `crossReference` both. Otherwise the refusal names the
 *   operation that last wrote the part of the funnel the first problem is in. The input is
 *   never touched, so a refusal leaves no partial funnel anywhere.
 *
 * WHAT AN OPERATION WRITES WHEN IT IS NOT TOLD is what the builder wrote for the same act
 * before Story 27.2 moved it onto these — `newBlock`'s minimums, `newFieldKey`'s keys,
 * `setOptionPoints`' criterion and weight, `setDisqualifying`'s gate, the criterion doors'
 * defaults — and since then there is one of each. A default the operation chooses is always
 * valid on its own; what it is TOLD, it writes as told, and the funnel judges.
 *
 * WHAT IS NOT HERE, AND WHY:
 *   - Removing anything. None of the eleven removes; the builder's deletes stay its own.
 *   - Settings and theme. No named operation writes them, so a proposal cannot either.
 *   - A number block's `step`. Its bound is exclusive (`positive()`), which `jsonSchemaOf`
 *     refuses to express rather than nudge. The builder never sets it on add.
 *   - An action's recipients and config. Who is told is the operator's to choose — RULE-067,
 *     and `defaultRouting`'s "NO RECIPIENTS" — and the builder's add sets neither.
 *
 * RULE-001: nothing here is named `index`; a place in a list is a `position`.
 */
import { z } from 'zod'
import {
  CADENCES,
  QUESTION_BLOCK_TYPES,
  acceptsExtraction,
  blockSchema,
  fieldKeySchema,
  localIdSchema,
  logicEffectSchema,
  optionIdSchema,
  optionSchema,
  outcomeSchema,
  parseFunnel,
  routingActionSchema,
  stepSchema,
  type ActionKind,
  type Block,
  type BlockInput,
  type BlockType,
  type Funnel,
  type FunnelInput,
  type LogicRule,
  type RoutingAction,
} from './funnel'
import {
  DERIVED_KEYS,
  EXTRACTION_FIELD_LIMIT,
  ICP_PROFILE_MAX,
  METADATA_KEYS,
  bandSchema,
  clipLabel,
  conditionSchema,
  extractionFieldObjectSchema,
  extractionFieldSchema,
  scoringMapOutcomes,
  scoringMapSchema,
  type Condition,
  type Criterion,
  type DerivedKey,
  type ExtractionField,
  type MetadataKey,
  type ScoringMap,
} from './rubric'
import { OFFERED_KINDS, newRoutingAction, withCadence } from './routing'

/* ── the vocabulary ─────────────────────────────────────────────────────── */

/** RULE-019's eleven, in the PRD's order: the nine M3 was planned with, then DECISION-177's two. */
export const OPERATION_NAMES = [
  'add_step',
  'add_question',
  'set_option',
  'set_criterion',
  'add_branch',
  'set_band',
  'set_outcome',
  'set_persona',
  'add_action',
  'add_extraction_field',
  'set_icp_profile',
] as const
export type OperationName = (typeof OPERATION_NAMES)[number]

/**
 * The longest sequence `applyOperations` takes. A generous ceiling rather than a budget: a
 * complete one-shot funnel is under a hundred operations, and the funnel's own limits — 500
 * blocks, 512 KB — still judge what the operations build.
 */
export const MAX_OPERATIONS = 500

/** The weight a criterion starts at — the builder's `DEFAULT_WEIGHT` (`editor/rubric.ts`). */
export const DEFAULT_CRITERION_WEIGHT = 25

/** The palette's word for each type, which is also what a new question asks until it is written. */
export const BLOCK_LABEL: Record<BlockType, string> = {
  card_select: 'Card select',
  dropdown: 'Dropdown',
  short_text: 'Short text',
  long_text: 'Long text',
  email: 'Email',
  phone: 'Phone',
  name: 'Name',
  number: 'Number',
  currency: 'Currency',
  scale: 'Scale',
  date: 'Date',
  hidden_field: 'Hidden field',
  consent: 'Consent',
  intro: 'Intro',
  divider: 'Divider',
  rich_text: 'Rich text',
  outcome: 'Outcome',
}

/** What a criterion reads, as far as its points care — an extraction field's type and bounds. */
export type Reads = Pick<ExtractionField, 'type' | 'min' | 'max' | 'enum_values'>

/** The ICP fit is a whole number from 0 to 100 (PRD §3.8), scored as a Number field is. */
export const DERIVED_READS: Record<DerivedKey, Reads> = {
  icp_fit: { type: 'number', min: 0, max: 100 },
}

/** What a derived criterion is called until someone renames it — the drawing's words. */
export const DERIVED_LABEL: Record<DerivedKey, string> = {
  icp_fit: 'Ideal customer match',
}

/**
 * A new extracted or derived criterion's points: its reading's shape, scoring zero for every
 * reading — DECISION-143, the operator enters every number. A One of scores each value, a
 * Yes/no keys `true` and `false` (never `presence`, which awards a "no" what a "yes" gets), a
 * Number is one range over its own bounds, and Text and Date are present or absent.
 */
export function emptyPointsFor(field: Reads): ScoringMap {
  switch (field.type) {
    case 'enum':
      return {
        kind: 'options',
        points_by_option: Object.fromEntries((field.enum_values ?? []).map((value) => [value, 0])),
        default_points: 0,
      }
    case 'boolean':
      return { kind: 'options', points_by_option: { true: 0, false: 0 }, default_points: 0 }
    case 'number':
      return {
        kind: 'ranges',
        ranges: [{ min: field.min ?? null, max: field.max ?? null, points: 0 }],
        default_points: 0,
      }
    case 'string':
    case 'date':
      return { kind: 'presence', points_present: 0, points_absent: 0 }
  }
}

/**
 * A field key that is legal on the first try: the type, then the type and a counter. The
 * builder's `newFieldKey`, without its random tail — below the funnel's 500 blocks a free
 * counter always exists before 1000, and past it this stays deterministic.
 */
export function newFieldKey(type: BlockType, taken: ReadonlySet<string>): string {
  const base = type.replace(/[^a-z0-9_]/g, '_')
  if (!taken.has(base)) return base
  let n = 2
  while (taken.has(`${base}_${n}`)) n += 1
  return `${base}_${n}`
}

/* ── the arguments ──────────────────────────────────────────────────────── */

const position = z.number().int().min(0).max(1000)
const points = z.number().int().min(0).max(1000)

type ArgsFor<T extends BlockType> = { type: T; id: string } & Partial<
  Omit<Extract<BlockInput, { type: T }>, 'type' | 'id' | 'step'>
>
/** A block as `add_question` takes it: its type and id, and whatever else it is told. */
export type BlockArgs = { [T in BlockType]: ArgsFor<T> }[BlockType]

/**
 * One block type's arguments: the funnel's own definition of that type, every field but
 * `type` and `id` optional — the operation writes the builder's defaults for the rest.
 * DERIVED rather than restated, so a field the funnel gains is a field the vocabulary can
 * set, with two changes:
 *
 *   - STRICT, down to its options. The funnel's objects strip an unknown key; here it is
 *     refused, because a model that writes an option's points inline — `{ id, label, points }`
 *     — would otherwise have them dropped without a word: the silent failure Story 19.1 made
 *     the analysis contract strict against.
 *   - No `step` (see the header).
 */
function argsVariant(variant: z.AnyZodObject): z.AnyZodObject {
  const shape: z.ZodRawShape = { ...variant.shape }
  delete shape['step']
  const options = shape['options']
  if (options instanceof z.ZodArray) {
    const min = options._def.minLength?.value
    const max = options._def.maxLength?.value
    let strict = z.array(optionSchema.strict())
    if (typeof min === 'number') strict = strict.min(min)
    if (typeof max === 'number') strict = strict.max(max)
    shape['options'] = strict
  }
  return z.object(shape).partial().required({ type: true, id: true }).strict()
}

export const blockArgsSchema = z.discriminatedUnion(
  'type',
  blockSchema.options.map(argsVariant) as unknown as [
    z.ZodObject<{ type: z.ZodLiteral<BlockType> }>,
    ...z.ZodObject<{ type: z.ZodLiteral<BlockType> }>[],
  ],
) as unknown as z.ZodType<BlockArgs>

/** A step, at a position — the end when none is given. */
export const addStepSchema = z
  .object({
    op: z.literal('add_step'),
    id: localIdSchema,
    title: stepSchema.shape.title,
    position: position.optional(),
  })
  .strict()

/** A block of any of the 17 types — a question or content — into a step. */
export const addQuestionSchema = z
  .object({
    op: z.literal('add_question'),
    step_id: localIdSchema,
    position: position.optional(),
    block: blockArgsSchema,
  })
  .strict()

/**
 * A label a person edits by typing, which passes through empty on the way to its next words
 * — the inspector's option names and the Qualify tab's band names are written on every
 * keystroke (Story 27.2). An empty one is the funnel's to refuse at save, as it always was;
 * refused here, the field would stop moving at its last character.
 */
const typedLabel = (max: number) => z.string().max(max)

/**
 * An option on a card select or dropdown, added or changed; with `points`, what it is worth
 * (the rubric owns that — B12); with `disqualifies`, whether choosing it is a hard gate.
 */
export const setOptionSchema = z
  .object({
    op: z.literal('set_option'),
    block_id: localIdSchema,
    id: optionIdSchema,
    label: typedLabel(300).optional(),
    description: optionSchema.shape.description,
    points: points.optional(),
    disqualifies: z.boolean().optional(),
  })
  .strict()

/**
 * A key in an options map: an option's id, `true` or `false` for a Yes/no, or one of an
 * extraction field's own values — which the rubric bounds by nothing, so neither does this
 * beyond sense. `__proto__` is refused for the reason `optionIdSchema` gives.
 */
const pointKey = z
  .string()
  .min(1)
  .max(2000)
  .refine((value) => value !== '__proto__', { message: '`__proto__` cannot be scored — it would be dropped from the map' })

/**
 * A scoring map in a shape a model can write: option points as a list rather than a keyed
 * record, which `jsonSchemaOf` refuses and strict structured-output modes cannot express.
 * Converted to the funnel's own map and checked by `scoringMapSchema` before it is written;
 * `pointsArgsOf` is the way back, for a door that already holds a map (Story 27.2).
 */
const pointsArgsSchema = z
  .object({
    kind: z.enum(['options', 'ranges', 'keywords', 'presence']),
    options: z
      .array(z.object({ option_id: pointKey, points }).strict())
      .max(200)
      .optional(),
    ranges: z
      .array(z.object({ min: z.number().nullable(), max: z.number().nullable(), points }).strict())
      .min(1)
      .max(50)
      .optional(),
    rules: z
      .array(
        z
          .object({
            any_of: z.array(z.string().min(1).max(200)).min(1).max(50),
            min_length: z.number().int().min(0).max(20000).optional(),
            points,
          })
          .strict(),
      )
      .min(1)
      .max(50)
      .optional(),
    points_present: points.optional(),
    points_absent: points.optional(),
    default_points: points.optional(),
  })
  .strict()
export type PointsArgs = z.infer<typeof pointsArgsSchema>

/**
 * A funnel scoring map as `set_criterion`'s points — for a door that computed the whole map,
 * as the extracted-points editor does. `toScoringMap` turns it back into the same map.
 */
export function pointsArgsOf(map: ScoringMap): PointsArgs {
  switch (map.kind) {
    case 'options':
      return {
        kind: 'options',
        options: Object.entries(map.points_by_option).map(([option_id, value]) => ({ option_id, points: value })),
        default_points: map.default_points,
      }
    case 'ranges':
      return { kind: 'ranges', ranges: map.ranges.map((range) => ({ ...range })), default_points: map.default_points }
    case 'keywords':
      return {
        kind: 'keywords',
        rules: map.rules.map((rule) => ({ ...rule, any_of: [...rule.any_of] })),
        default_points: map.default_points,
      }
    case 'presence':
      return { kind: 'presence', points_present: map.points_present, points_absent: map.points_absent }
  }
}

const CRITERION_KEYS = [...DERIVED_KEYS, ...METADATA_KEYS] as [
  DerivedKey | MetadataKey,
  ...(DerivedKey | MetadataKey)[],
]

/**
 * A criterion, added or changed. Found by `id` when one is given, otherwise by what it reads
 * — so "score this question" twice is one criterion, not two. `transcript` is M5 and absent.
 */
export const setCriterionSchema = z
  .object({
    op: z.literal('set_criterion'),
    id: localIdSchema.optional(),
    source: z.enum(['answer_ref', 'extracted', 'derived', 'metadata']),
    block_id: localIdSchema.optional(),
    field_key: fieldKeySchema.optional(),
    key: z.enum(CRITERION_KEYS).optional(),
    label: z.string().min(1).max(200).optional(),
    weight: z.number().int().min(1).max(1000).optional(),
    points: pointsArgsSchema.optional(),
  })
  .strict()

/**
 * A logic rule — "if … then …" — in the funnel's own condition and effect definitions, so the
 * rule a model writes and the rule the Logic tab writes are one shape.
 */
export const addBranchSchema = z
  .object({
    op: z.literal('add_branch'),
    id: localIdSchema,
    step_id: localIdSchema.optional(),
    when: conditionSchema,
    then: logicEffectSchema,
    position: position.optional(),
  })
  .strict()

/** A band, added or changed. A new one needs every field; the ladder is the funnel's to judge. */
export const setBandSchema = z
  .object({
    op: z.literal('set_band'),
    id: localIdSchema,
    label: typedLabel(100).optional(),
    min: bandSchema.shape.min.optional(),
    max: bandSchema.shape.max.optional(),
    tone: bandSchema.shape.tone.optional(),
    is_qualified: bandSchema.shape.is_qualified.optional(),
  })
  .strict()

/**
 * An http or https address. Stricter than the funnel's own `url()`, which a `javascript:`
 * address passes: an outcome's button is a link on the respondent's page, and here the author
 * may be a model.
 */
const webAddress = z
  .string()
  .max(2000)
  .url()
  .regex(/^https?:\/\//, 'an http or https address')

/** What a band's respondents see at the end, added or changed. */
export const setOutcomeSchema = z
  .object({
    op: z.literal('set_outcome'),
    band_id: localIdSchema,
    heading: outcomeSchema.shape.heading.optional(),
    body: z.string().max(4000).optional(),
    cta: z.object({ label: z.string().min(1).max(200), url: webAddress }).strict().optional(),
    redirect_url: webAddress.optional(),
  })
  .strict()

/** A voice persona for a band (M5, PRD §3.14), added or changed. No control draws it before the Voice tab. */
export const setPersonaSchema = z
  .object({
    op: z.literal('set_persona'),
    id: localIdSchema,
    band_id: localIdSchema.optional(),
    name: z.string().min(1).max(200).optional(),
    instructions: z.string().max(8000).optional(),
  })
  .strict()

/** A routing action on a band — of a kind the builder offers today, to nobody until the operator says. */
export const addActionSchema = z
  .object({
    op: z.literal('add_action'),
    id: localIdSchema,
    band_id: localIdSchema,
    kind: z.enum(OFFERED_KINDS as unknown as [ActionKind, ...ActionKind[]]),
    cadence: z.enum(CADENCES).optional(),
    cadence_at: routingActionSchema.shape.cadence_at,
    cadence_day: routingActionSchema.shape.cadence_day,
  })
  .strict()

/**
 * What to read out of a free-text answer — RULE-021's "extract budget as a number and timeline as
 * one of these four values": a named, typed field over a question in `ai_extraction` mode. The
 * funnel's own keys, the key among them, because the criterion that scores the field names it
 * (`set_criterion`, `source: 'extracted'`); the extraction card derives it from the name before it
 * calls this (`fieldKeyFor`, `editor/extraction.ts`), as the palette's ids are made before
 * `add_question`.
 */
export const addExtractionFieldSchema = z
  .object({ op: z.literal('add_extraction_field'), ...extractionFieldObjectSchema.shape })
  .strict()

/**
 * Who the ideal customer is, in words (PRD §3.8, DECISION-138) — what the analysis measures a
 * lead's fit against. Never itself scored: a `derived` criterion is what makes the fit count.
 * The whole description, replaced, as the Qualify tab's field writes it on every keystroke.
 */
export const setIcpProfileSchema = z
  .object({
    op: z.literal('set_icp_profile'),
    description: z.string().max(ICP_PROFILE_MAX),
  })
  .strict()

/** The vocabulary: one union, the definition every reader shares. */
export const operationSchema = z.discriminatedUnion('op', [
  addStepSchema,
  addQuestionSchema,
  setOptionSchema,
  setCriterionSchema,
  addBranchSchema,
  setBandSchema,
  setOutcomeSchema,
  setPersonaSchema,
  addActionSchema,
  addExtractionFieldSchema,
  setIcpProfileSchema,
])
export type Operation = z.infer<typeof operationSchema>

/** A sequence, as a proposal carries one. */
export const operationListSchema = z.array(operationSchema).max(MAX_OPERATIONS)

/* ── applying one ───────────────────────────────────────────────────────── */

export type OperationOutcome = { ok: true } | { ok: false; reason: string }

/** Records which part of the funnel an operation wrote, so a sequence can name who broke what. */
type Touch = (key: string) => void

const OK: OperationOutcome = { ok: true }
const refuse = (reason: string): OperationOutcome => ({ ok: false, reason })
const nothing: Touch = () => {}

/**
 * One operation, applied in place to a draft — a recipe for `edit()`.
 *
 * Refuses an argument of the wrong type, an id that names nothing and an id already taken,
 * and writes nothing when it does. Never throws for any input.
 */
export function applyOperation(draft: Funnel, input: unknown): OperationOutcome {
  const read = readOperation(input)
  if (!read.ok) return read
  return perform(draft, read.operation, nothing)
}

function readOperation(input: unknown): { ok: true; operation: Operation } | { ok: false; reason: string } {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    return { ok: false, reason: `an operation is an object naming its op — one of ${OPERATION_NAMES.join(', ')}` }
  }
  const name = (input as { op?: unknown }).op
  if (typeof name !== 'string' || !(OPERATION_NAMES as readonly string[]).includes(name)) {
    const said = typeof name === 'string' ? `"${name.slice(0, 40)}"` : 'an operation with no op'
    return { ok: false, reason: `${said} is not in the vocabulary — use one of ${OPERATION_NAMES.join(', ')}` }
  }
  const parsed = operationSchema.safeParse(input)
  if (parsed.success) return { ok: true, operation: parsed.data }
  const issue = parsed.error.issues[0]
  const where = issue?.path.join('.') ?? ''
  return { ok: false, reason: `${name}: ${where === '' ? '' : `${where} — `}${issue?.message ?? 'these arguments do not fit it'}` }
}

function perform(draft: Funnel, op: Operation, touch: Touch): OperationOutcome {
  switch (op.op) {
    case 'add_step':
      return addStep(draft, op, touch)
    case 'add_question':
      return addQuestion(draft, op, touch)
    case 'set_option':
      return setOption(draft, op, touch)
    case 'set_criterion':
      return setCriterion(draft, op, touch)
    case 'add_branch':
      return addBranch(draft, op, touch)
    case 'set_band':
      return setBand(draft, op, touch)
    case 'set_outcome':
      return setOutcome(draft, op, touch)
    case 'set_persona':
      return setPersona(draft, op, touch)
    case 'add_action':
      return addAction(draft, op, touch)
    case 'add_extraction_field':
      return addExtractionField(draft, op)
    case 'set_icp_profile':
      return setIcpProfile(draft, op)
  }
}

type Op<N extends OperationName> = Extract<Operation, { op: N }>

/* ── add_step ── */

function addStep(draft: Funnel, op: Op<'add_step'>, touch: Touch): OperationOutcome {
  if (draft.steps.some((step) => step.id === op.id)) return refuse(`a step with id "${op.id}" already exists`)
  const at = op.position ?? draft.steps.length
  if (at > draft.steps.length) {
    return refuse(`the funnel has ${draft.steps.length} step(s), so a new one goes at a position from 0 to ${draft.steps.length}`)
  }
  // Named by where it LANDS, as the builder names it: an insert at position 1 is "Step 2".
  draft.steps.splice(at, 0, { id: op.id, title: op.title ?? `Step ${at + 1}`, blocks: [] })
  touch(`step:${op.id}`)
  touch('steps')
  return OK
}

/* ── add_question ── */

function addQuestion(draft: Funnel, op: Op<'add_question'>, touch: Touch): OperationOutcome {
  const step = draft.steps.find((candidate) => candidate.id === op.step_id)
  if (step === undefined) return refuse(`no step with id "${op.step_id}" — add it with add_step first`)
  const args = given(op.block as Record<string, unknown>) as BlockArgs
  if (draft.blocks.some((block) => block.id === args.id)) return refuse(`a block with id "${args.id}" already exists`)
  const at = op.position ?? step.blocks.length
  if (at > step.blocks.length) {
    return refuse(`step "${step.id}" shows ${step.blocks.length} block(s), so a new one goes at a position from 0 to ${step.blocks.length}`)
  }
  if ('field_key' in args && typeof args.field_key === 'string') {
    const owner = draft.blocks.find((block) => 'field_key' in block && block.field_key === args.field_key)
    if (owner !== undefined) {
      return refuse(`field_key "${args.field_key}" is already used by block "${owner.id}" — a field key names one answer`)
    }
  }
  if ('options' in args && Array.isArray(args.options)) {
    const seen = new Set<string>()
    for (const option of args.options) {
      if (seen.has(option.id)) return refuse(`two options share the id "${option.id}" — an option id is the answer, so each names one`)
      seen.add(option.id)
    }
  }

  const block = blockWithDefaults(args, fieldKeys(draft))
  const checked = blockSchema.safeParse(block)
  if (!checked.success) {
    const issue = checked.error.issues[0]
    return refuse(`block "${args.id}": ${issue?.path.join('.') ?? ''} — ${issue?.message ?? 'not a block the funnel accepts'}`)
  }
  draft.blocks.push(block as Block)
  step.blocks.splice(at, 0, args.id)
  touch(`block:${args.id}`)
  touch(`step:${step.id}`)
  touch('blocks')
  return OK
}

/**
 * The block the palette makes for a type, with what the operation was told laid over it. The
 * defaults are the schema's minimums and nothing more: a question needs words, a choice needs
 * one option, a currency its code, a scale its ends. The builder's `newBlock` is this with a
 * fresh id (Story 27.2), so a block has one definition of new.
 */
export function blockWithDefaults(args: BlockArgs, taken: ReadonlySet<string>): BlockInput {
  const type = args.type
  switch (type) {
    case 'hidden_field':
      // Not a question: no `question`, no `required`, and `source` left to its default.
      return { field_key: newFieldKey(type, taken), ...args } as BlockInput
    case 'intro':
      return { heading: 'Before we start', ...args } as BlockInput
    case 'rich_text':
      return { body: 'Text', ...args } as BlockInput
    case 'divider':
    case 'outcome':
      return { ...args } as BlockInput
    default:
      break
  }
  const question = { field_key: newFieldKey(type, taken), question: BLOCK_LABEL[type], required: false }
  // The first option's id is local to its block, as every option id is: `opt_1` in each.
  const first = [{ id: 'opt_1', label: 'First option' }]
  switch (type) {
    case 'card_select':
      return { ...question, selection: 'single', options: first, ...args } as BlockInput
    case 'dropdown':
      return { ...question, options: first, ...args } as BlockInput
    case 'short_text':
      return { ...question, max_length: 200, free_text_mode: 'capture_only', ...args } as BlockInput
    case 'long_text':
      return { ...question, max_length: 5000, free_text_mode: 'capture_only', ...args } as BlockInput
    case 'currency':
      return { ...question, currency_code: 'USD', ...args } as BlockInput
    case 'scale':
      return { ...question, min: 1, max: 5, ...args } as BlockInput
    case 'consent':
      return { ...question, statement: 'I agree.', ...args } as BlockInput
    default:
      return { ...question, ...args } as BlockInput
  }
}

/* ── set_option ── */

function setOption(draft: Funnel, op: Op<'set_option'>, touch: Touch): OperationOutcome {
  const block = draft.blocks.find((candidate) => candidate.id === op.block_id)
  if (block === undefined) return refuse(`no block with id "${op.block_id}"`)
  if (block.type !== 'card_select' && block.type !== 'dropdown') {
    return refuse(`"${block.id}" is a ${block.type} block — only card_select and dropdown blocks have options`)
  }
  const existing = block.options.find((option) => option.id === op.id)
  if (existing === undefined && op.label === undefined) {
    return refuse(`"${block.id}" has no option "${op.id}" yet, and a new option needs a label`)
  }
  const label = op.label ?? existing?.label ?? op.id
  const gateId = `dq_${block.id}_${op.id}`
  if (op.disqualifies === true) {
    const clash = draft.rubric.disqualifiers.find(
      (gate) => gate.id === gateId && !isOptionGate(gate.when, block.id, op.id),
    )
    if (clash !== undefined) return refuse(`a disqualifier with id "${gateId}" already exists and gates something else`)
  }

  if (existing === undefined) {
    block.options.push({ id: op.id, label, ...(op.description !== undefined ? { description: op.description } : {}) })
  } else {
    if (op.label !== undefined) existing.label = op.label
    if (op.description !== undefined) existing.description = op.description
  }
  if (op.points !== undefined) scoreOption(draft, block, op.id, op.points, touch)
  if (op.disqualifies !== undefined) gateOption(draft, block, op.id, label, op.disqualifies, touch)
  touch(`block:${block.id}`)
  return OK
}

/**
 * `setOptionPoints`: what one option is worth, creating the block's criterion the first time
 * an option is worth anything — never for a zero, so adding a question does not silently start
 * scoring it — and raising the weight with a point that passes it, as the inspector does.
 */
function scoreOption(draft: Funnel, block: Block, optionId: string, value: number, touch: Touch): void {
  const safe = Math.max(0, Math.round(value))
  let criterion = draft.rubric.criteria.find(
    (candidate) => candidate.source === 'answer_ref' && candidate.block_id === block.id,
  )
  if (criterion === undefined) {
    if (safe === 0) return
    draft.rubric.criteria.push({
      id: freeCriterionId(draft, `crit_${block.id}`),
      label: clipLabel('question' in block ? block.question : block.id),
      weight: Math.max(DEFAULT_CRITERION_WEIGHT, safe),
      source: 'answer_ref',
      block_id: block.id,
      points: { kind: 'options', points_by_option: {}, default_points: 0 },
    })
    criterion = draft.rubric.criteria[draft.rubric.criteria.length - 1]!
  }
  // The block gained options after being scored some other way: a `ranges` map has nothing
  // to say about an option id, so replacing it is the only coherent move (the builder's too).
  if (criterion.points.kind !== 'options') {
    criterion.points = { kind: 'options', points_by_option: {}, default_points: 0 }
  }
  criterion.points.points_by_option[optionId] = safe
  if (safe > criterion.weight) criterion.weight = safe
  touch(`criterion:${criterion.id}`)
  touch('criteria')
  touch('ceiling')
}

const isOptionGate = (when: Condition, blockId: string, optionId: string): boolean =>
  when.subject.kind === 'answer' &&
  when.subject.block_id === blockId &&
  when.operator === 'is' &&
  when.value === optionId

/** `setDisqualifying`: choosing this option is a hard gate, or stops being one (RULE-027). */
function gateOption(draft: Funnel, block: Block, optionId: string, optionLabel: string, on: boolean, touch: Touch): void {
  const at = draft.rubric.disqualifiers.findIndex((gate) => isOptionGate(gate.when, block.id, optionId))
  touch('disqualifiers')
  if (!on) {
    if (at >= 0) draft.rubric.disqualifiers.splice(at, 1)
    return
  }
  if (at >= 0) return
  const id = `dq_${block.id}_${optionId}`
  draft.rubric.disqualifiers.push({
    id,
    label: clipLabel(`${'question' in block ? block.question : block.id} — ${optionLabel}`),
    when: { subject: { kind: 'answer', block_id: block.id }, operator: 'is', value: optionId },
  })
  touch(`disqualifier:${id}`)
}

/* ── set_criterion ── */

type Target =
  | { source: 'answer_ref'; block: Block }
  | { source: 'extracted'; field: ExtractionField }
  | { source: 'derived'; key: DerivedKey }
  | { source: 'metadata'; key: MetadataKey }

function setCriterion(draft: Funnel, op: Op<'set_criterion'>, touch: Touch): OperationOutcome {
  const read = targetOf(draft, op)
  if (!read.ok) return read
  const target = read.target
  const reads = (criterion: Criterion): boolean => readsSame(criterion, target)

  const existing =
    op.id !== undefined
      ? draft.rubric.criteria.find((criterion) => criterion.id === op.id)
      : draft.rubric.criteria.find(reads)
  if (existing !== undefined && !reads(existing)) {
    return refuse(`criterion "${existing.id}" scores ${describeReading(existing)} — give this one another id`)
  }
  if (existing === undefined && op.id !== undefined) {
    const other = draft.rubric.criteria.find(reads)
    if (other !== undefined) return refuse(`${describeTarget(target)} is already scored by criterion "${other.id}" — set that one`)
  }

  let map: ScoringMap | undefined
  if (op.points !== undefined) {
    const converted = toScoringMap(op.points)
    if (!converted.ok) return converted
    map = converted.map
    const shaped = fitsReading(target, map)
    if (!shaped.ok) return shaped
  }

  if (existing !== undefined) {
    if (op.label !== undefined) existing.label = op.label
    if (map !== undefined) existing.points = map
    if (op.weight !== undefined) existing.weight = op.weight
    else raiseToPoints(existing)
    touch(`criterion:${existing.id}`)
    touch('criteria')
    touch('ceiling')
    return OK
  }

  const label = op.label ?? defaultLabel(target)
  if (label === undefined) return refuse('a metadata criterion needs a label — the words the evidence rows say')
  if (map === undefined) {
    const fallback = defaultPoints(target)
    if (!fallback.ok) return fallback
    map = fallback.map
  }
  const id = op.id ?? freeCriterionId(draft, `crit_${referenceOf(target)}`)
  const created = {
    id,
    label,
    weight: op.weight ?? DEFAULT_CRITERION_WEIGHT,
    points: map,
    ...readingOf(target),
  } as Criterion
  // Told a weight, it is written as told and the funnel judges it; left to choose one, the
  // operation chooses one its own points fit under, as the inspector does.
  if (op.weight === undefined) raiseToPoints(created)
  draft.rubric.criteria.push(created)
  touch(`criterion:${id}`)
  touch('criteria')
  touch('ceiling')
  return OK
}

function targetOf(
  draft: Funnel,
  op: Op<'set_criterion'>,
): { ok: true; target: Target } | { ok: false; reason: string } {
  const stray = (fields: ('block_id' | 'field_key' | 'key')[]): string[] =>
    fields.filter((field) => op[field] !== undefined)
  switch (op.source) {
    case 'answer_ref': {
      const extra = stray(['field_key', 'key'])
      if (extra.length > 0) return { ok: false, reason: `an answer_ref criterion reads a block — drop ${extra.join(' and ')}` }
      if (op.block_id === undefined) return { ok: false, reason: 'an answer_ref criterion names the block it scores in block_id' }
      const block = draft.blocks.find((candidate) => candidate.id === op.block_id)
      if (block === undefined) return { ok: false, reason: `no block with id "${op.block_id}"` }
      if (!(QUESTION_BLOCK_TYPES as readonly string[]).includes(block.type)) {
        return { ok: false, reason: `"${block.id}" is a ${block.type} block — it produces no answer to score` }
      }
      return { ok: true, target: { source: 'answer_ref', block } }
    }
    case 'extracted': {
      const extra = stray(['block_id', 'key'])
      if (extra.length > 0) return { ok: false, reason: `an extracted criterion reads a field — drop ${extra.join(' and ')}` }
      if (op.field_key === undefined) return { ok: false, reason: 'an extracted criterion names the field it scores in field_key' }
      const field = draft.rubric.extraction.find((candidate) => candidate.field_key === op.field_key)
      if (field === undefined) {
        return { ok: false, reason: `no extraction field named "${op.field_key}" — define it under rubric.extraction first` }
      }
      return { ok: true, target: { source: 'extracted', field } }
    }
    case 'derived': {
      const extra = stray(['block_id', 'field_key'])
      if (extra.length > 0) return { ok: false, reason: `a derived criterion reads a value about the whole submission — drop ${extra.join(' and ')}` }
      const key = op.key ?? 'icp_fit'
      if (!(DERIVED_KEYS as readonly string[]).includes(key)) {
        return { ok: false, reason: `"${key}" is not a derived value — a derived criterion reads ${DERIVED_KEYS.join(', ')}` }
      }
      if (draft.rubric.icp_profile.trim() === '') {
        return { ok: false, reason: 'a derived criterion scores the match against icp_profile, and icp_profile is empty' }
      }
      return { ok: true, target: { source: 'derived', key: key as DerivedKey } }
    }
    case 'metadata': {
      const extra = stray(['block_id', 'field_key'])
      if (extra.length > 0) return { ok: false, reason: `a metadata criterion reads attribution — drop ${extra.join(' and ')}` }
      if (op.key === undefined || !(METADATA_KEYS as readonly string[]).includes(op.key)) {
        return { ok: false, reason: `a metadata criterion names one of ${METADATA_KEYS.join(', ')} in key` }
      }
      return { ok: true, target: { source: 'metadata', key: op.key as MetadataKey } }
    }
  }
}

function readsSame(criterion: Criterion, target: Target): boolean {
  switch (target.source) {
    case 'answer_ref':
      return criterion.source === 'answer_ref' && criterion.block_id === target.block.id
    case 'extracted':
      return criterion.source === 'extracted' && criterion.field_key === target.field.field_key
    case 'derived':
      return criterion.source === 'derived' && criterion.key === target.key
    case 'metadata':
      return criterion.source === 'metadata' && criterion.key === target.key
  }
}

/** The criterion's reading fields, as the funnel stores them. */
function readingOf(target: Target): Record<string, string> {
  switch (target.source) {
    case 'answer_ref':
      return { source: 'answer_ref', block_id: target.block.id }
    case 'extracted':
      return { source: 'extracted', field_key: target.field.field_key }
    case 'derived':
      return { source: 'derived', key: target.key }
    case 'metadata':
      return { source: 'metadata', key: target.key }
  }
}

const referenceOf = (target: Target): string =>
  target.source === 'answer_ref' ? target.block.id : target.source === 'extracted' ? target.field.field_key : target.key

function describeTarget(target: Target): string {
  switch (target.source) {
    case 'answer_ref':
      return `block "${target.block.id}"`
    case 'extracted':
      return `field "${target.field.field_key}"`
    case 'derived':
      return `the derived value ${target.key}`
    case 'metadata':
      return `metadata ${target.key}`
  }
}

function describeReading(criterion: Criterion): string {
  switch (criterion.source) {
    case 'answer_ref':
      return `block "${criterion.block_id}"`
    case 'extracted':
    case 'transcript':
      return `field "${criterion.field_key}"`
    case 'derived':
      return `the derived value ${criterion.key}`
    case 'metadata':
      return `metadata ${criterion.key}`
  }
}

/** The label each of the builder's doors writes: the question, the field's name, the drawing's. */
function defaultLabel(target: Target): string | undefined {
  switch (target.source) {
    case 'answer_ref':
      return clipLabel('question' in target.block ? target.block.question : target.block.id)
    case 'extracted':
      return target.field.label
    case 'derived':
      return DERIVED_LABEL[target.key]
    case 'metadata':
      return undefined
  }
}

/**
 * The map each of the builder's doors writes: an empty options map for a question an options
 * map can score (Story 7.1's door), the field's own shape at zero for an extracted or derived
 * reading (DECISION-143). A question no door scores has no default: its points are asked for.
 */
function defaultPoints(target: Target): { ok: true; map: ScoringMap } | { ok: false; reason: string } {
  switch (target.source) {
    case 'answer_ref': {
      const type = target.block.type
      if (type === 'card_select' || type === 'dropdown' || type === 'consent') {
        return { ok: true, map: { kind: 'options', points_by_option: {}, default_points: 0 } }
      }
      return {
        ok: false,
        reason: `a ${type} question needs its points — ranges for number, currency and scale, keywords for text, presence for the rest`,
      }
    }
    case 'extracted':
      return { ok: true, map: emptyPointsFor(target.field) }
    case 'derived':
      return { ok: true, map: emptyPointsFor(DERIVED_READS[target.key]) }
    case 'metadata':
      return { ok: false, reason: 'a metadata criterion needs its points' }
  }
}

/** The map each reading's type is scored by — DECISION-143's shapes, `emptyPointsFor`'s kinds. */
const READING_KIND: Record<Reads['type'], ScoringMap['kind']> = {
  enum: 'options',
  boolean: 'options',
  number: 'ranges',
  string: 'presence',
  date: 'presence',
}

/**
 * An extracted or derived reading's points, in the shape its type takes (DECISION-143): a One of
 * scores its own values, a Yes/no `true` and `false`, a Number by ranges, Text and Date present or
 * absent — what the extracted-points editor writes, `fitted` in `editor/extracted-points.ts`.
 *
 * The FUNNEL takes another shape, deliberately: drafts carrying one exist, and the editor declines
 * to offer a wrong shape rather than make a draft unreadable. An OPERATION is refused one, because
 * a value it scores that the field never reads awards nothing and says nothing — DECISION-067's
 * silent failure — and a model that misspells a value writes exactly that (Story 27.5). An answer's
 * criterion needs no check here: `crossReference` refuses an option its question does not offer.
 */
function fitsReading(target: Target, map: ScoringMap): OperationOutcome {
  if (target.source !== 'extracted' && target.source !== 'derived') return OK
  const reads: Reads = target.source === 'extracted' ? target.field : DERIVED_READS[target.key]
  const what = target.source === 'extracted' ? `field "${target.field.field_key}"` : `the derived value ${target.key}`
  const kind = READING_KIND[reads.type]
  if (map.kind !== kind) return refuse(`${what} (type ${reads.type}) is scored with kind "${kind}", not "${map.kind}"`)
  if (map.kind === 'options') {
    const values = reads.type === 'boolean' ? ['true', 'false'] : (reads.enum_values ?? [])
    const stray = Object.keys(map.points_by_option).find((key) => !values.includes(key))
    if (stray !== undefined) {
      const listed = values.length > 12 ? `${values.slice(0, 12).join(', ')}, …` : values.join(', ')
      return refuse(`${what} never reads "${stray}" — score one of its values: ${listed}`)
    }
  }
  return OK
}

/** Raise a criterion's weight to the most its own points can award, as `setOptionPoints` does. */
function raiseToPoints(criterion: Criterion): void {
  const top = Math.max(...scoringMapOutcomes(criterion.points))
  if (top > criterion.weight) criterion.weight = top
}

const MAP_FIELDS: Record<PointsArgs['kind'], (keyof PointsArgs)[]> = {
  options: ['options', 'default_points'],
  ranges: ['ranges', 'default_points'],
  keywords: ['rules', 'default_points'],
  presence: ['points_present', 'points_absent'],
}

function toScoringMap(args: PointsArgs): { ok: true; map: ScoringMap } | { ok: false; reason: string } {
  const allowed = MAP_FIELDS[args.kind]
  const stray = (['options', 'ranges', 'rules', 'points_present', 'points_absent', 'default_points'] as const).filter(
    (field) => args[field] !== undefined && !allowed.includes(field),
  )
  if (stray.length > 0) {
    return { ok: false, reason: `a ${args.kind} map takes ${allowed.join(' and ')}, not ${stray.join(' or ')}` }
  }
  let candidate: unknown
  switch (args.kind) {
    case 'options': {
      const seen = new Set<string>()
      for (const entry of args.options ?? []) {
        if (seen.has(entry.option_id)) return { ok: false, reason: `option "${entry.option_id}" is scored twice in one map` }
        seen.add(entry.option_id)
      }
      candidate = {
        kind: 'options',
        points_by_option: Object.fromEntries((args.options ?? []).map((entry) => [entry.option_id, entry.points])),
        default_points: args.default_points ?? 0,
      }
      break
    }
    case 'ranges':
      if (args.ranges === undefined) return { ok: false, reason: 'a ranges map needs its ranges' }
      candidate = { kind: 'ranges', ranges: args.ranges, default_points: args.default_points ?? 0 }
      break
    case 'keywords':
      if (args.rules === undefined) return { ok: false, reason: 'a keywords map needs its rules' }
      candidate = { kind: 'keywords', rules: args.rules, default_points: args.default_points ?? 0 }
      break
    case 'presence':
      if (args.points_present === undefined) return { ok: false, reason: 'a presence map needs points_present' }
      candidate = { kind: 'presence', points_present: args.points_present, points_absent: args.points_absent ?? 0 }
      break
  }
  const parsed = scoringMapSchema.safeParse(candidate)
  if (!parsed.success) {
    const issue = parsed.error.issues[0]
    return { ok: false, reason: `points: ${issue?.path.join('.') ?? ''} — ${issue?.message ?? 'not a scoring map'}` }
  }
  return { ok: true, map: parsed.data }
}

/* ── add_branch ── */

function addBranch(draft: Funnel, op: Op<'add_branch'>, touch: Touch): OperationOutcome {
  if (draft.logic.some((rule) => rule.id === op.id)) return refuse(`a rule with id "${op.id}" already exists`)
  const blockOf = (id: string): Block | undefined => draft.blocks.find((block) => block.id === id)
  const stepExists = (id: string): boolean => draft.steps.some((step) => step.id === id)

  const subject = op.when.subject
  if (subject.kind === 'answer' && blockOf(subject.block_id) === undefined) {
    return refuse(`the rule reads block "${subject.block_id}", and there is no block with that id`)
  }
  if (subject.kind === 'extracted' && !draft.rubric.extraction.some((field) => field.field_key === subject.field_key)) {
    return refuse(`the rule reads the field "${subject.field_key}", and no extraction field has that key`)
  }
  if (op.step_id !== undefined && !stepExists(op.step_id)) return refuse(`no step with id "${op.step_id}"`)

  const effect = op.then
  switch (effect.action) {
    case 'show':
    case 'hide':
      if (blockOf(effect.target_block_id) === undefined) return refuse(`the rule would ${effect.action} block "${effect.target_block_id}", and there is no such block`)
      break
    case 'skip_to':
      if (!stepExists(effect.target_step_id)) return refuse(`the rule skips to step "${effect.target_step_id}", and there is no such step`)
      break
    case 'set_hidden_field': {
      const target = blockOf(effect.target_block_id)
      if (target === undefined) return refuse(`the rule writes block "${effect.target_block_id}", and there is no such block`)
      if (target.type !== 'hidden_field') return refuse(`"${target.id}" is a ${target.type} block — set_hidden_field writes a hidden_field`)
      break
    }
    case 'disqualify':
      break
  }

  const at = op.position ?? draft.logic.length
  if (at > draft.logic.length) {
    return refuse(`the funnel has ${draft.logic.length} rule(s), so a new one goes at a position from 0 to ${draft.logic.length}`)
  }
  // The builder files a rule under the step its question sits on; told a step, it uses that.
  const stepId =
    op.step_id ??
    (subject.kind === 'answer'
      ? draft.steps.find((step) => step.blocks.includes(subject.block_id))?.id
      : undefined)
  const rule = { id: op.id, ...(stepId !== undefined ? { step_id: stepId } : {}), when: op.when, then: op.then } as LogicRule
  draft.logic.splice(at, 0, rule)
  touch(`rule:${op.id}`)
  touch('logic')
  return OK
}

/* ── set_band ── */

function setBand(draft: Funnel, op: Op<'set_band'>, touch: Touch): OperationOutcome {
  const band = draft.rubric.bands.find((candidate) => candidate.id === op.id)
  if (band !== undefined) {
    if (op.label !== undefined) band.label = op.label
    if (op.min !== undefined) band.min = op.min
    if (op.max !== undefined) band.max = op.max
    if (op.tone !== undefined) band.tone = op.tone
    if (op.is_qualified !== undefined) band.is_qualified = op.is_qualified
  } else {
    const { label, min, max, tone, is_qualified } = op
    if (label === undefined || min === undefined || max === undefined || tone === undefined || is_qualified === undefined) {
      const missing = (['label', 'min', 'max', 'tone', 'is_qualified'] as const).filter((field) => op[field] === undefined)
      return refuse(`there is no band "${op.id}" yet, and a new band needs its ${missing.join(', ')}`)
    }
    draft.rubric.bands.push({ id: op.id, label, min, max, tone, is_qualified })
  }
  // A band can leave the ladder gapped or overlapping, and it can leave the top short of what
  // the criteria award — the second is also a weight's doing, so it has its own key.
  touch('bands')
  touch('ceiling')
  return OK
}

/* ── set_outcome ── */

function setOutcome(draft: Funnel, op: Op<'set_outcome'>, touch: Touch): OperationOutcome {
  const band = draft.rubric.bands.find((candidate) => candidate.id === op.band_id)
  if (band === undefined) return refuse(`no band with id "${op.band_id}"`)
  const outcome = draft.outcomes.find((candidate) => candidate.band_id === op.band_id)
  if (outcome !== undefined) {
    if (op.heading !== undefined) outcome.heading = op.heading
    if (op.body !== undefined) outcome.body = op.body
    if (op.cta !== undefined) outcome.cta = op.cta
    if (op.redirect_url !== undefined) outcome.redirect_url = op.redirect_url
  } else {
    if (op.heading === undefined) return refuse(`band "${band.id}" has no outcome yet, and a new outcome needs a heading`)
    draft.outcomes.push({
      band_id: op.band_id,
      heading: op.heading,
      body: op.body ?? '',
      ...(op.cta !== undefined ? { cta: op.cta } : {}),
      ...(op.redirect_url !== undefined ? { redirect_url: op.redirect_url } : {}),
    })
  }
  touch(`outcome:${op.band_id}`)
  touch('outcomes')
  return OK
}

/* ── set_persona ── */

function setPersona(draft: Funnel, op: Op<'set_persona'>, touch: Touch): OperationOutcome {
  if (op.band_id !== undefined && !draft.rubric.bands.some((band) => band.id === op.band_id)) {
    return refuse(`no band with id "${op.band_id}"`)
  }
  const persona = draft.voice.personas.find((candidate) => candidate.id === op.id)
  if (persona !== undefined) {
    if (op.band_id !== undefined) persona.band_id = op.band_id
    if (op.name !== undefined) persona.name = op.name
    if (op.instructions !== undefined) persona.instructions = op.instructions
  } else {
    const { band_id, name, instructions } = op
    if (band_id === undefined || name === undefined || instructions === undefined) {
      const missing = (['band_id', 'name', 'instructions'] as const).filter((field) => op[field] === undefined)
      return refuse(`there is no persona "${op.id}" yet, and a new persona needs its ${missing.join(', ')}`)
    }
    draft.voice.personas.push({ id: op.id, band_id, name, instructions })
  }
  touch(`persona:${op.id}`)
  touch('personas')
  return OK
}

/* ── add_action ── */

function addAction(draft: Funnel, op: Op<'add_action'>, touch: Touch): OperationOutcome {
  if (!draft.rubric.bands.some((band) => band.id === op.band_id)) return refuse(`no band with id "${op.band_id}"`)
  if (draft.routing.some((rule) => rule.actions.some((action) => action.id === op.id))) {
    return refuse(`an action with id "${op.id}" already exists`)
  }
  // `newRoutingAction` IS the builder's: instant, to nobody, enabled. A cadence goes through
  // `withCadence`, which fills the time a digest needs; a time or day it is told is written as
  // told, and the funnel judges whether that cadence takes one.
  let action: RoutingAction = newRoutingAction(op.id, op.kind)
  if (op.cadence !== undefined) action = withCadence(action, op.cadence)
  if (op.cadence_at !== undefined) action = { ...action, cadence_at: op.cadence_at }
  if (op.cadence_day !== undefined) action = { ...action, cadence_day: op.cadence_day }

  const rule = draft.routing.find((candidate) => candidate.band_id === op.band_id)
  if (rule !== undefined) rule.actions.push(action)
  else draft.routing.push({ band_id: op.band_id, actions: [action] })
  touch(`action:${op.id}`)
  touch(`routing:${op.band_id}`)
  touch('routing')
  return OK
}

/* ── add_extraction_field ── */

/**
 * `addExtractionField`, the extraction card's "Add a field": refused at the rubric's cap or over a
 * key it holds, as the card's recipe was, and over anything but a question in `ai_extraction` mode
 * — "adding a long-text field does not automatically hand it to the AI" (PRD §3.6), so a field
 * never puts a question there itself. What the card refuses before it calls, this refuses too: a
 * range or a set of values travels only with the type that takes one, a range runs upwards, and a
 * value is listed once.
 *
 * It touches no key: every problem the funnel could find in a field is refused here first, so no
 * later refusal can be this operation's doing — and neither can one be `set_icp_profile`'s.
 */
function addExtractionField(draft: Funnel, op: Op<'add_extraction_field'>): OperationOutcome {
  const { op: _name, ...field } = op
  const extraction = draft.rubric.extraction
  if (extraction.length >= EXTRACTION_FIELD_LIMIT) {
    return refuse(`the rubric already reads ${EXTRACTION_FIELD_LIMIT} fields, the most it holds`)
  }
  if (extraction.some((existing) => existing.field_key === field.field_key)) {
    return refuse(`an extraction field with key "${field.field_key}" already exists`)
  }
  const block = draft.blocks.find((candidate) => candidate.id === field.block_id)
  if (block === undefined) return refuse(`the field reads block "${field.block_id}", and there is no block with that id`)
  if (block.type !== 'short_text' && block.type !== 'long_text') {
    return refuse(`"${block.id}" is a ${block.type} block — a field reads a short_text or long_text question`)
  }
  if (!acceptsExtraction(block)) {
    return refuse(`"${block.id}" is in ${block.free_text_mode} mode — a field reads a question whose free_text_mode is ai_extraction`)
  }
  if ((field.min !== undefined || field.max !== undefined) && field.type !== 'number') {
    return refuse(`min and max bound a number field, and "${field.field_key}" is a ${field.type} field`)
  }
  if (field.min !== undefined && field.max !== undefined && field.min > field.max) {
    return refuse(`min ${field.min} is above max ${field.max}, so no number fits the field`)
  }
  const repeated = field.enum_values?.find((value, at, values) => values.indexOf(value) !== at)
  if (repeated !== undefined) return refuse(`"${repeated}" is listed twice in enum_values — each value is one the reading can take`)

  const checked = extractionFieldSchema.safeParse(field)
  if (!checked.success) {
    const issue = checked.error.issues[0]
    return refuse(`field "${field.field_key}": ${issue?.path.join('.') ?? ''} — ${issue?.message ?? 'not a field the rubric accepts'}`)
  }
  extraction.push(checked.data)
  return OK
}

/* ── set_icp_profile ── */

/**
 * `setIcpProfile`, the Qualify tab's "Describe your ideal customer". Emptying it while a criterion
 * scores the fit is refused, as the tab refuses it (Story 20.9): the fit is measured against the
 * description, so the rubric would refuse the criterion at the next save. Whitespace is empty — the
 * rubric trims.
 */
function setIcpProfile(draft: Funnel, op: Op<'set_icp_profile'>): OperationOutcome {
  if (op.description.trim() === '') {
    const reader = draft.rubric.criteria.find((criterion) => criterion.source === 'derived')
    if (reader !== undefined) {
      return refuse(`criterion "${reader.id}" scores the match against icp_profile — it cannot be emptied while that criterion reads it`)
    }
  }
  draft.rubric.icp_profile = op.description
  return OK
}

/* ── applying a sequence ────────────────────────────────────────────────── */

/**
 * Why a sequence was refused: which operation — its `position` in the sequence, from 0, and its
 * name — or `null` when the refusal is about the sequence as a whole.
 */
export type Refusal = { position: number | null; op: OperationName | null; reason: string }
export type SequenceOutcome = { ok: true; funnel: Funnel } | { ok: false; refusal: Refusal }

/**
 * A sequence, applied to a copy, and the funnel it builds — or why not.
 *
 * Refused whole: every operation is read before any is applied, the starting funnel must
 * itself be valid, and the result is returned only when `parseFunnel` accepts it. Pure: the
 * funnel passed in is never changed, so nothing partial exists after a refusal. Never throws.
 */
export function applyOperations(funnel: FunnelInput, operations: unknown): SequenceOutcome {
  if (!Array.isArray(operations)) return whole('a proposal is a list of operations')
  const total = operations.length
  if (total > MAX_OPERATIONS) {
    return whole(`a proposal carries at most ${MAX_OPERATIONS} operations, and this one has ${total}`)
  }

  const read: Operation[] = []
  for (let at = 0; at < total; at += 1) {
    const raw: unknown = operations[at]
    const one = readOperation(raw)
    if (!one.ok) return refusedAt(at, total, nameOf(raw), one.reason)
    read.push(one.operation)
  }

  const start = parseFunnel(funnel)
  if (!start.ok) {
    const issue = start.issues[0]
    return whole(`the funnel was not valid before the first operation: ${issue?.path ?? ''} — ${issue?.message ?? ''}`)
  }

  const draft = structuredClone(start.funnel)
  const wrote = new Map<string, number>()
  for (let at = 0; at < total; at += 1) {
    const operation = read[at]!
    let done: OperationOutcome
    try {
      done = perform(draft, operation, (key) => wrote.set(key, at))
    } catch {
      // Unreachable by design, and loud if not: a test that meets this sentence fails.
      done = refuse('could not be applied')
    }
    if (!done.ok) return refusedAt(at, total, operation.op, done.reason)
  }

  const result = parseFunnel(draft)
  if (result.ok) return { ok: true, funnel: result.funnel }

  const issue = result.issues[0]!
  const key = keyOf(issue.path, issue.message, draft)
  const at = (key !== null ? wrote.get(key) : undefined) ?? total - 1
  if (at < 0) return whole(issue.message)
  const where = issue.path === '' ? '' : ` (at ${issue.path})`
  return refusedAt(at, total, read[at]!.op, `${issue.message}${where}`)
}

function whole(reason: string): SequenceOutcome {
  return { ok: false, refusal: { position: null, op: null, reason } }
}

function refusedAt(at: number, total: number, op: OperationName | null, reason: string): SequenceOutcome {
  return {
    ok: false,
    refusal: { position: at, op, reason: `operation ${at + 1} of ${total}${op === null ? '' : ` (${op})`}: ${reason}` },
  }
}

function nameOf(raw: unknown): OperationName | null {
  if (typeof raw !== 'object' || raw === null) return null
  const name = (raw as { op?: unknown }).op
  return typeof name === 'string' && (OPERATION_NAMES as readonly string[]).includes(name) ? (name as OperationName) : null
}

/**
 * Which part of the funnel a validation path is in, in the keys the operations write — so the
 * refusal names the operation that last wrote it. `null` for a part no operation writes; the
 * sequence's last operation is named then.
 */
function keyOf(path: string, message: string, draft: Funnel): string | null {
  const parts = path.split('.')
  const at = (n: number): number => Number(parts[n])
  const deeper = parts.length > 1
  switch (parts[0]) {
    case 'steps':
      return deeper ? `step:${draft.steps[at(1)]?.id}` : 'steps'
    case 'blocks':
      return deeper ? `block:${draft.blocks[at(1)]?.id}` : 'blocks'
    case 'logic':
      return deeper ? `rule:${draft.logic[at(1)]?.id}` : 'logic'
    case 'outcomes':
      return deeper ? `outcome:${draft.outcomes[at(1)]?.band_id}` : 'outcomes'
    case 'routing':
      if (parts.length > 3 && parts[2] === 'actions') return `action:${draft.routing[at(1)]?.actions[at(3)]?.id}`
      return deeper ? `routing:${draft.routing[at(1)]?.band_id}` : 'routing'
    case 'voice':
      if (parts[1] !== 'personas') return null
      return parts.length > 2 ? `persona:${draft.voice.personas[at(2)]?.id}` : 'personas'
    case 'rubric':
      // Only bands gap or overlap; a top that cannot reach the rubric's ceiling is a band's
      // doing or a weight's, whichever moved last (`validateBandLadder`'s own sentence).
      if (parts[1] === 'bands') return message.startsWith('the highest band ends at') ? 'ceiling' : 'bands'
      if (parts[1] === 'criteria') return parts.length > 2 ? `criterion:${draft.rubric.criteria[at(2)]?.id}` : 'criteria'
      if (parts[1] === 'disqualifiers') {
        return parts.length > 2 ? `disqualifier:${draft.rubric.disqualifiers[at(2)]?.id}` : 'disqualifiers'
      }
      // No case for `extraction` or `icp_profile`: every problem the funnel could find there, the
      // operation refuses first, and no later operation changes a question's mode (Story 27.5).
      return null
    default:
      return null
  }
}

/* ── helpers ────────────────────────────────────────────────────────────── */

/** An object without its undefined entries, so a default is never overwritten by nothing. */
function given(value: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined))
}

/** Every field key the funnel's blocks hold. */
function fieldKeys(draft: Funnel): Set<string> {
  return new Set(draft.blocks.flatMap((block) => ('field_key' in block ? [block.field_key] : [])))
}

/** `base`, or the first free `base_N` — criterion ids are unique across every door. */
function freeCriterionId(draft: Funnel, base: string): string {
  const taken = new Set(draft.rubric.criteria.map((criterion) => criterion.id))
  if (!taken.has(base)) return base
  let n = 2
  while (taken.has(`${base}_${n}`)) n += 1
  return `${base}_${n}`
}
